<template lang="pug">
.custom-job-sites-rule-wrapper.p-15.mb-sm-15
  .d-flex.align-items-center.justify-content-between
    h5 Group {{ rule.number }}
    //- Add trash icon if removable
    template(v-if="removable")
      a.btn-delete-rule.pull-right(
        href="#"
        @click.prevent="$emit('remove')"
      )
        .hi.hi-10.hi-delete.text-danger
  .basic-filters
    //- Teams & Members select
    ValidationProvider(
      v-slot="{ errors, invalid }"
      slim
      :vid="`jobSiteMembers-${rule.number}`"
      :rules="membersValidation"
      :custom-messages="{ notIn: DENIED_TOOLTIP }"
    )
      NewJobSitesMemberTeamsSelect.mb-5(
        :value="selectedTeamsMembers"
        :has-error="invalid && !!errors.length"
        :teams-enabled="teamsEnabled"
        :options="teamsAndMembersOptions"
        :selected-project-id="rule.start_project_id"
        @input="onMembersTeamsSelect"
      )
        //- #teams-addon
        template(#label)
          template(v-if="teamsEnabled") Teams & Members
          template(v-else) Members
        //- We do this, so we can avoid prop drilling anymore than necessary.
        //- But also to avoid having to much complexity in such a small and
        //- nested leaf component. The power of `v-slot` allows us to pass
        //- a "function" to the parent and call it with the parent's data.
        template(#select-all="{ onSelectAll }")
          a.pull-right.select-all(
            href="#"
            :disabled="isSelectAllDisabled"
            @click.prevent="!isSelectAllDisabled && onSelectAll(allIds)"
          )
            | Select all
        template(
          v-if="invalid && errors.length"
          #error
        )
          //- This will be the `DENIED_TOOLTIP` constant from below.
          span.help-block {{ errors[0] }}
      //- Show the option to change member's location tracking setting
      //- if it's turned off for some of the selected members.
      SelectClientLocationTrackingSetting(
        v-if="!!membersWithTrackingOff.length"
        :members="membersWithTrackingOff"
      )
      //- Info about team members being viewers
      //- #teams-addon
      .viewer-info-text(v-if="teamsEnabled && viewersViaTeams.length")
        i.fa.fa-info-circle.mr-5
        | Some members are Project Viewers and the timer actions will not be applied.
    //- Actions
    .mt-15
      p.mb-0
        span.timer-actions(
          :class="{ 'has-error': isProjectRequired && !rule.start_project_id }"
          @click="expanded = !expanded"
        )
          i.hi-caret.mr-10(:class="{ expanded }")
          | Automatic timer actions
      .px-20(v-show="expanded")
        AppToggleButtonGroup.mt-15(
          label="On arrival"
          :items="JOB_SITE_ENTER_ACTIONS"
          :selected="rule.enter_action"
          @input="onEnterActionSelect($event)"
        )
        //- NOTE: We use `immediate` because if the user selects the "Start timer"
        //- we want to show feedback tell them that a project MUST be selected.
        ValidationProvider(
          v-slot="{ errors, invalid }"
          slim
          immediate
          :vid="`jobSiteProject-${rule.number}`"
          :rules="isProjectRequired ? 'required' : ''"
        )
          //- Project select
          NewJobSitesProjectSelect(
            :required="isProjectRequired"
            :teams-enabled="teamsEnabled"
            :value="rule.start_project_id"
            :selected-teams-members="selectedTeamsMembers"
            :disabled="disableProjectSelect"
            :has-error="!!(invalid && errors.length)"
            @input="onProjectSelect"
          )
            template(
              v-if="invalid && errors.length"
              #error
            )
              span.help-block {{ errors[0] }}
        AppToggleButtonGroup(
          label="On departure"
          :items="JOB_SITE_EXIT_ACTIONS"
          :selected="rule.exit_action"
          @input="onExitActionSelect($event)"
        )
      //- Actions summary when not expanded
      .summary-timer-actions.mt-10.px-20(v-show="!expanded")
        .d-flex.align-items-center.mb-5
          label.control-label.m-0 On arrival:
          span {{ actionsText.enter }}
        .d-flex.align-items-center
          label.control-label.m-0 On departure:
          span {{ actionsText.exit }}
</template>

<script>
import {
  CONSTANTS,
  JOB_SITE_ACTIONS,
  JOB_SITE_EXIT_ACTIONS,
  JOB_SITE_ENTER_ACTIONS,
  USER_PROJECT_ROLE_VIEWER,
} from '~/app_constants';
import { orderBy } from 'lodash-es';
import { mapGetters } from 'vuex';
import NewJobSitesProjectSelect from './NewJobSitesProjectSelect.vue';
import NewJobSitesMemberTeamsSelect from './NewJobSitesMemberTeamsSelect.vue';
import { getIdsForOptionType, partitionTeamsMembersIds } from '~/utils/job_sites';
import SelectClientLocationTrackingSetting from './SelectClientLocationTrackingSetting.vue';

const DENIED_TOOLTIP = CONSTANTS.viewerProjectMemberDeniedTooltip;

export default {
  name: 'NewJobSiteRule',
  components: {
    NewJobSitesProjectSelect,
    NewJobSitesMemberTeamsSelect,
    SelectClientLocationTrackingSetting,
  },
  props: {
    rule: {
      type: Object,
      required: true,
    },
    expandedByDefault: {
      type: Boolean,
      default: false,
    },
    removable: {
      type: Boolean,
      default: false,
    },
    teamsForSelect: {
      type: Array,
      required: true,
    },
    membersForSelect: {
      type: Array,
      required: true,
    },
    // This will be the combination of the explicitly selected members,
    // and implicitly selected members (via teams).
    usedMemberIds: {
      type: Set,
      required: true,
    },
    teamsEnabled: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      DENIED_TOOLTIP,
      JOB_SITE_EXIT_ACTIONS,
      JOB_SITE_ENTER_ACTIONS,
      teamMembersSelected: [],
      expanded: this.expandedByDefault,
      disabledTooltipOptions: Object.freeze({
        placement: 'top',
      }),
    };
  },
  computed: {
    ...mapGetters(['allTeamsMap', 'allMembersMap', 'activeProjects']),
    allIds() {
      // In order for the select-all button to work, we need to
      // compute the list of all valid team & member ids.
      // #teams-addon
      const allTeamIds = this.teamsEnabled
        ? // Only compute the temas ids if teams addon is enabled
          this.allTeamsForSelect
            // Filter out the dummy option for nested empty results
            .filter((option) => option.id !== 'no_teams')
            .map((team) => team.id)
        : [];
      const allMemberIds = this.allMembersForSelect
        // Filter out the dummy option for nested empty results
        .filter((option) => option.id !== 'no_members')
        .map((member) => member.id);

      // If the user hasn't selected a project, we don't need to
      // worry about the members that are not in the project
      // or that are simply viewers.
      if (!this.rule.start_project_id) {
        return [...allTeamIds, ...allMemberIds];
      }

      return [
        ...allTeamIds,
        // Remove the viewer ids from the list of all ids
        ...allMemberIds.filter((id) => !this.viewerUserIds.includes(id)),
      ];
    },
    viewerUserIds() {
      // If the user hasn't selected a project, we don't need to worry
      // about the members that are `VIEWER`s in the project.
      if (!this.rule.start_project_id) {
        return [];
      }

      // Otherwise, we need to filter out the members that are `VIEWER`s
      // in the project and return the ids of those members.
      const project = this.activeProjects.find((project) => project.id === this.rule.start_project_id);

      return project && project.user_role_ids
        ? Object.keys(project.user_role_ids)
            .filter((userId) => project.user_role_ids[userId] === USER_PROJECT_ROLE_VIEWER)
            .map((userId) => getIdsForOptionType(userId, 'member').id)
        : [];
    },
    viewersViaTeams() {
      const teamIds = this.rule.team_ids || [];

      return teamIds.reduce((ids, teamId) => {
        const team = this.allTeamsMap.get(teamId);
        const viewerIds = team.user_ids.filter((userId) =>
          this.viewerUserIds.includes(getIdsForOptionType(userId, 'member').id),
        );

        ids.push(...viewerIds);

        return ids;
      }, []);
    },
    selectedTeamsMembers() {
      // #teams-addon
      if (this.teamsEnabled) {
        const [{ children: teamOptions }, { children: memberOptions }] = this.teamsAndMembersOptions;

        return [
          ...teamOptions.filter((option) => this.rule.team_ids.includes(option._id)).map((option) => option.id),
          ...memberOptions.filter((option) => this.rule.user_ids.includes(option._id)).map((option) => option.id),
        ];
      }

      return this.teamsAndMembersOptions
        .filter((option) => this.rule.user_ids.includes(option._id))
        .map((option) => option.id);
    },
    teamsUsedUserIds() {
      return new Set(this.rule.team_ids.flatMap((teamId) => this.allTeamsMap.get(teamId).user_ids));
    },
    // This is the list of teams for the select, it takes the list
    // passed from the parent (excluding used teams), and adds the
    // teams that are already selected in "this rule".
    allTeamsForSelect() {
      // Avoid uneccessary computation if the teams addon
      // is disabled. Until we deal with this more calmly.
      // #teams-addon
      if (!this.teamsEnabled) {
        return [];
      }

      const disabledTeamsAddon = {
        // Used by `AppSelect` to show the already selected option
        // as an error and add a tooltip.
        type: 'disabled-teams-addon',
        hint: CONSTANTS.jobSitesDisabledTeamsAddonTooltip,
        // Used by the `AppSelect` to disable the option and show the tooltip.
        disabled: true,
        tooltipOptions: this.disabledTooltipOptions,
        disabledHint: CONSTANTS.jobSitesDisabledTeamsAddonTooltip,
      };
      const teams = new Map(
        this.teamsForSelect.map((team) => {
          const ids = getIdsForOptionType(team.id, 'team');

          return [
            ids._id,
            {
              ...team,
              ...ids,
              // Add the warning, disable state and tooltip if
              // the teams addon is disabled.
              // #teams-addon
              ...(this.teamsEnabled ? {} : disabledTeamsAddon),
            },
          ];
        }),
      );
      const { team_ids: teamIds } = this.rule;

      // For each user id in the rule, add it to the
      // `members` map if it's not already present.
      if (teamIds && teamIds.length) {
        for (const teamId of teamIds) {
          const ids = getIdsForOptionType(teamId, 'team');

          if (!teams.has(ids._id)) {
            teams.set(ids._id, {
              ...ids,
              text: this.allTeamsMap.get(teamId).name,
              // Add the warning, disable state and tooltip if
              // the teams addon is disabled.
              // #teams-addon
              ...(this.teamsEnabled ? {} : disabledTeamsAddon),
            });
          }
        }
      }

      // Because nested select options don't work with `noResults` configuration
      // setting for Select2, we need to work around it.
      if (!teams.size) {
        const emptyTeam = {
          id: 'no_teams',
          disabled: true,
          text: this.allTeamsMap.size
            ? // Check if we had teams originally, if so,
              // return the appropriate message.
              'All teams are already added to this job site'
            : 'No teams available',
        };

        teams.set('no_options', emptyTeam);
      }

      return orderBy([...teams.values()], [(team) => team.text.toUpperCase()], ['asc']);
    },
    // Because of the way the parent is in charge of the "used ids"
    // and some other data in order to know whether or not should display
    // part of the UI, the `NewJobSiteRule` needs to be responsible to
    // re-add the "used ids" to the `membersForSelect` for its own rule.
    allMembersForSelect() {
      const disabledOptions = {
        // Used by `AppSelect` to show the already selected option
        // as an error and add a tooltip.
        type: 'error',
        hint: DENIED_TOOLTIP,
        // Used by the `AppSelect` to disable the option and show the tooltip.
        disabled: true,
        disabledHint: DENIED_TOOLTIP,
        tooltipOptions: this.disabledTooltipOptions,
      };
      // Options for disabling members because they're selected via a team
      const disabledViaTeamsOptions = {
        disabled: true,
        tooltipOptions: this.disabledTooltipOptions,
        disabledHint: 'This member is already added via a team.',
      };
      // Create a map for easy lookup of the "membersForSelect"
      // And while doing so, make sure we have the correct `disabled`
      // state for that member.
      const members = new Map(
        this.membersForSelect.map((member) => {
          const ids = getIdsForOptionType(member.id, 'member');
          const isViewer = this.viewerUserIds.includes(ids.id);
          const isTeamsDisabled = this.teamsUsedUserIds.has(ids._id);

          return [
            ids._id,
            {
              ...member,
              ...ids,
              ...(isTeamsDisabled ? disabledViaTeamsOptions : {}),
              // Only add the warning if the user is not already present
              // in a team; as we will show the warning separately.
              ...(isViewer && !isTeamsDisabled ? disabledOptions : {}),
            },
          ];
        }),
      );
      const { user_ids: userIds } = this.rule;
      // For each user id in the rule, add it to the
      // `members` map if it's not already present.
      const addMember = (userId, viaTeams = false) => {
        const user = this.allMembersMap.get(userId);

        // Skip the project viewers that are selected for display
        if (!user) {
          return;
        }

        const ids = getIdsForOptionType(userId, 'member');
        // We don't want to show the error for the viewer option if we're
        // processing users from the `teams.user_ids`.
        const isViewer = !viaTeams ? this.viewerUserIds.includes(ids.id) : false;
        const disabled = viaTeams ? disabledViaTeamsOptions : {};

        members.set(ids._id, {
          ...ids,
          ...disabled,
          ...(isViewer ? disabledOptions : {}),
          text: user.name,
        });
      };

      if (userIds && userIds.length) {
        for (const userId of userIds) {
          if (!members.has(userId)) {
            addMember(userId, false);
          }
        }
      }

      // Override options, making sure that we have the correct `disabled` state
      // for team members that are already selected.
      this.teamsUsedUserIds.forEach((userId) => addMember(userId, true));

      // Because nested select options don't work with `noResults` configuration
      // setting for Select2, we need to work around it.
      if (!members.size) {
        const emptyMember = {
          id: 'no_members',
          disabled: true,
          text: this.allMembersMap.size
            ? // Check if we had members originally, if so,
              // return the appropriate message.
              'All members are already added to this job site'
            : 'No members available',
        };

        members.set('no_options', emptyMember);
      }

      // Don't order by `disabled` first because the user might've selected
      // a viewer member, and then this executes, the list in the list
      // "selection" will re-order itself, and thus creating a bad UX.
      return orderBy([...members.values()], [(member) => member.text.toUpperCase()], ['asc']);
    },
    teamsAndMembersOptions() {
      // #teams-addon
      if (this.teamsEnabled) {
        return [
          {
            text: 'Teams',
            children: this.allTeamsForSelect,
          },
          {
            text: 'Members',
            children: this.allMembersForSelect,
          },
        ];
      }

      return this.allMembersForSelect;
    },
    disableProjectSelect() {
      return this.rule.enter_action !== JOB_SITE_ACTIONS.trackTime;
    },
    membersValidation() {
      const rules = { required: true };

      // If we have a project selected with some users that
      // have the `VIEWER` role, we need to validate that
      // we don't allow the selection of any of them.
      if (this.viewerUserIds.length) {
        rules.notIn = { list: this.viewerUserIds };
      }

      return rules;
    },
    isProjectRequired() {
      return this.rule.enter_action === JOB_SITE_ACTIONS.trackTime;
    },
    isSelectAllDisabled() {
      const { user_ids: userIds, team_ids: teamIds } = this.rule;
      // We need to not count all the members that are already `disabled` because of the
      // teams they're in, or because they're viewers of the selected project.
      const notDisabledMembers = this.allMembersForSelect.filter((member) => !member.disabled);

      // #teams-addon
      if (this.teamsEnabled) {
        return this.allTeamsForSelect.length === teamIds.length && notDisabledMembers.length === userIds.length;
      }

      return notDisabledMembers.length === userIds.length;
    },
    // Find the members with the location tracking setting off
    membersWithTrackingOff() {
      const members = [];

      this.usedMemberIds.forEach((memberId) => {
        const member = this.allMembersMap.get(memberId);

        // Skip it if the member is not selected for display
        if (member && member.client_location_tracking_off) {
          members.push(member);
        }
      });

      return members;
    },
    actionsText() {
      const exitAction = JOB_SITE_EXIT_ACTIONS.find((action) => action.value === this.rule.exit_action);
      const enterAction = JOB_SITE_ENTER_ACTIONS.find((action) => action.value === this.rule.enter_action);

      return {
        exit: exitAction ? exitAction.text : '',
        enter: enterAction ? enterAction.text : '',
      };
    },
  },
  methods: {
    onMembersTeamsSelect(selected) {
      const rule = { ...this.rule };
      const [teamsOptIds, membersOptIds] = partitionTeamsMembersIds(selected);
      const parseEntityId = (entityId) => parseInt(entityId.split('-')[1], 10);
      const teams = new Map(
        teamsOptIds.map((teamOptId) => {
          const teamId = parseEntityId(teamOptId);

          return [teamId, this.allTeamsMap.get(teamId)];
        }),
      );
      const teamsUserIds = [...teams.values()].flatMap((team) => team.user_ids);

      // Because the Project select depends on the teams/members
      // selected, reset it if we don't have any.
      if (!selected || !selected.length) {
        rule.start_project_id = null;
      }

      rule.team_ids = [...teams.keys()];
      rule.user_ids = membersOptIds.reduce(
        // We only care if the type is member, and make sure we automatically
        // remove user ids that are already part of a team.
        (ids, memberOptId) => {
          const memberId = parseEntityId(memberOptId);

          return !teamsUserIds.includes(memberId) ? [...ids, memberId] : ids;
        },
        [],
      );

      this.$emit('update', rule);
    },
    onProjectSelect(projectId) {
      const rule = { ...this.rule };

      rule.start_project_id = projectId ? parseInt(projectId, 10) : null;
      this.$emit('update', rule);
    },
    onEnterActionSelect(action) {
      const rule = { ...this.rule };

      rule.enter_action = action;
      // Also update the exit action whenever the enter action changes
      rule.exit_action = action;
      // Update the `start_project_id`
      rule.start_project_id = null;

      this.$emit('update', rule);
    },
    onExitActionSelect(action) {
      const rule = { ...this.rule };

      rule.exit_action = action;
      // If the exit action is set to `trackTime`, we need to update the
      // `enter_action` to `trackTime` as well.
      if (action === JOB_SITE_ACTIONS.trackTime) {
        rule.enter_action = action;
      }

      this.$emit('update', rule);
    },
  },
};
</script>
